import _ from 'lodash';
import $ from 'jquery';
import ReactiveRegister from 'javascripts/reactive_register';
import Patcher from './reactive_rails/Patcher';
import { updateReactiveParams } from './reactive_partial_params';
import * as Sentry from '@sentry/browser';
import { type RecordId } from 'javascripts/shared-types';
import makeDebugger from 'javascripts/util/devLog';

const REACTIVE_UPDATE_REQUEST = 'reactive-update-request';

const debug =
  window.railsEnv === 'test' ? console.debug : makeDebugger('reactive_rails');

type Change = {
  action: 'create' | 'update' | 'destroy';
  path: string;
};

type ReactiveMatch = {
  change: Change;
  element: Element;
};

type Options = {
  sharedAccountId?: RecordId;
  sharedResourceId?: RecordId;
};

function elementIdentifier(el: Element): string {
  if (!el) return '';

  return [
    el.tagName?.toLowerCase(),
    el.id && `#${el.id}`,
    el.className?.trim() && `.${el.className.split(/\s+/).join('.')}`,
  ].join('');
}

class Reactor {
  client: any; // Faye does not have types
  userNotificationRegex: RegExp;
  sharedAccountId?: RecordId;
  sharedResourceId?: RecordId;

  throttledUpdates = {};

  constructor(client, options: Options = {}) {
    this.client = client;
    this.sharedResourceId = options.sharedResourceId;
    this.sharedAccountId = options.sharedAccountId;
    this.subscribe();
    this.userNotificationRegex = new RegExp(
      `User-${$('meta[name=user-id]').attr('content')}/Notification-`
    );
  }

  clientId() {
    return this.client._dispatcher.clientId;
  }

  subscribe = () => {
    const accountToken = $('meta[name=account-id]').attr('content');

    const url =
      this.sharedAccountId && this.sharedResourceId
        ? `/shared-reactive/listen/${this.sharedAccountId}/${this.sharedResourceId}`
        : `/reactive/listen/${accountToken}`;

    this.client.subscribe(url, message => {
      this.processChanges(message.changes, message.source_client_id);
    });
  };

  // We want to determine the minimum set of changes by finding any
  // common parents, and deleting any duplicated changes.
  processChanges(changes: Change[], sourceClientId: string) {
    let change: Change, element: Element;
    const ownChanges = sourceClientId === this.clientId();
    let matchingElements: ReactiveMatch[] = [];

    debug('Changes:');
    changes.forEach(change => {
      debug(`  Got ${change.action} for ${change.path}`);

      // User notifications are only for each individual user, so we can skip them if they don't match the current user
      if (
        !change.path.match(/User-\d+\/Notification-/) ||
        change.path.match(this.userNotificationRegex)
      ) {
        // Send to any registered handlers.
        ReactiveRegister.dispatch(change, ownChanges, sourceClientId);

        // Add the containers first.
        $('[data-reactive-path]').each(function () {
          const element = $(this);
          if (ownChanges && element.is('[data-reactive-ignore-own-changes]')) {
            debug(
              '    Ignoring changes due to data-reactive-ignore-own-changes'
            );
            return;
          }
          if (element.is('[data-reactive-stubbed-content="true"]')) {
            debug('    Skipping stubbed content');
            return;
          }

          const paths = element.attr('data-reactive-path');
          if (paths.length === 0) {
            debug('    Skipping element without a reactive path');
            return;
          }

          for (let path of paths.split(',')) {
            if (path.length === 0) {
              continue;
            }

            if (change.path.indexOf(path) !== -1) {
              debug(`    Matched element: ${elementIdentifier(this)}`);

              matchingElements.push({ change, element: element[0] });
            }
          }
        });

        // Reduce to list of unique containers.
        matchingElements = _.uniqBy(matchingElements, ({ element }) => element);

        const parts = change.path.split('/');
        const record = parts[parts.length - 1];
        $('[data-reactive-record="' + record + '"]').each(function () {
          const element = this;
          if (
            ownChanges &&
            $(element).is('[data-reactive-ignore-own-changes]')
          ) {
            debug(
              '    Skipping data-reactive-record due to data-reactive-ignore-own-changes'
            );
            return;
          }

          // Just remove the element.
          if (change.action === 'destroy') {
            const reactiveRecordContainer = element.closest(
              '[data-reactive-url][data-reactive-record-container="true"]'
            );
            debug('    Removing reactive record due to destroy');
            $(element).remove();

            if (reactiveRecordContainer) {
              debug(
                '    Updating data-reactive-record-container because contained record was destroyed'
              );
              matchingElements.push({
                change: {
                  action: 'update',
                  path: '',
                },
                element: reactiveRecordContainer,
              });
            }

            return;
          }

          // Add elements.
          debug(`    Matched reactive record: ${elementIdentifier(this)}`);
          matchingElements.push({ change, element });
        });
      }
    });

    // Send to any batch registered handlers.
    ReactiveRegister.dispatchBatch(changes, ownChanges, sourceClientId);

    matchingElements = _.uniqBy(matchingElements, ({ element }) => element);

    // Remove elements that are contained by other elements in the
    // matchingElements array, except where there is a data-reative-preserve
    // fence. This optimizes the calls we need to make to the server in cases
    // where there are nested reactive elements. This algorithm is O(N^2) so
    // hopefully we don't have too many elements we need to check.
    for (let index = matchingElements.length - 1; index >= 0; index--) {
      ({ element } = matchingElements[index]);
      let parentFound = false;

      // Look for parents.
      for (let { element: parentElement } of matchingElements) {
        if (element === parentElement) {
          continue;
        }
        if ($.contains(parentElement, element)) {
          parentFound = true;
          // Found a parent, make sure there is no fence.
          $(element)
            .parentsUntil(parentElement)
            .each(function () {
              if ($(this).attr('data-reactive-preserve')) {
                parentFound = false;
              }
            });
        }
        if (parentFound) {
          debug(
            `    Removing element contained in another reactive element: ${elementIdentifier(
              element
            )}`
          );
          matchingElements.splice(index, 1);
          break;
        }
      }
    }

    $("[data-reactive-container='true']").each(function (_index, container) {
      const bulkableMatchingElements = matchingElements.filter(
        matchingElement => $(container).find(matchingElement.element).length
      );
      if (bulkableMatchingElements.length > 3) {
        debug(
          `Replacing ${
            bulkableMatchingElements.length
          } reactive requests with one request to ${$(container).data(
            'reactive-url'
          )}`
        );
        matchingElements = _.difference(
          matchingElements,
          bulkableMatchingElements
        );

        matchingElements.push({
          element: container,
          change: { action: 'update', path: '' },
        });
      }
    });

    for ({ element, change } of matchingElements) {
      this.update($(element), change, ownChanges);
    }
  }

  update = (element, change, ownChanges) => {
    const url = element.attr('data-reactive-url') || '';
    if (url.length === 0) {
      return;
    }

    try {
      if (
        new URL(url).pathname.startsWith('/shared/') &&
        (!this.sharedAccountId || !this.sharedResourceId)
      ) {
        // This Reactor can't handle shared resources, so ignore them
        debug(
          `    ${url}: Skipping shared resource with default reactive updater`
        );
        return;
      }
    } catch (e) {
      // Don't error on invalid URLs
    }

    // Don't call reactive update paths for a destroy action (they will 404).
    if (
      change.action === 'destroy' &&
      !element.is(
        "[data-reactive-allow-destroy], [data-reactive-record-container='true']"
      )
    ) {
      debug(
        `    ${url}: Skipping update for destroy: ${elementIdentifier(
          element[0]
        )}`
      );
      return;
    }

    if (
      ownChanges &&
      element.is('[data-reactive-show-own-changes-immediately]')
    ) {
      // Skip throttling if we want to see our own changes right
      // away. This prevents UI slowdowns, like delaying 5 seconds
      // before seeing the advanced equation editor.
      debug(`    ${url}: Unthrottled request`);
      this.loadReactiveUpdate(element, url);
    } else if (this.throttledUpdates[url]) {
      // Once a url has been called once it will throttle to once every 5 seconds
      debug(`    ${url}: Adding update to throttled request`);
      this.throttledUpdates[url](element, url);
    } else {
      let throttleSpeed = parseInt(
        element.attr('data-reactive-throttle-speed')
      );
      if (isNaN(throttleSpeed)) {
        throttleSpeed = window.railsEnv === 'test' ? 1 : 5000; // default throttle speed
      }

      // Create the throttled function, disable the first call since we will call it directly below
      debug(`    ${url}: Running throttled request (${throttleSpeed}ms)`);
      this.throttledUpdates[url] = _.throttle(
        (elementToUpdate, url) => this.loadReactiveUpdate(elementToUpdate, url),
        throttleSpeed
      );

      this.throttledUpdates[url](element, url);
    }
  };

  // Update a parent container of an element
  updateParentContainer(element) {
    const recordContainer = $(element).closest(
      '[data-reactive-url][data-reactive-record-container="true"]'
    );

    if (recordContainer[0]) {
      debug(
        `[REACTIVE] Updating parent container: ${elementIdentifier(element)}`
      );
      this.forceUpdateElement(recordContainer);
    }
  }

  // This should only be used in instances where we don't want to emit a reactive update, but we want to reload a partial
  // for just the current user.
  forceUpdateElement(element, url: string | null = null) {
    debug(`[REACTIVE] Forced update on element: ${elementIdentifier(element)}`);
    this.loadReactiveUpdate(element, url);
  }

  loadReactiveUpdate(element, url) {
    const request = Reactor.updateReactiveElement(element, url, {
      isShared: !!(this.sharedAccountId && this.sharedResourceId),
    });

    request.always(() => {
      element.remove('.reactive-in-progress');
    });

    return request;
  }

  // Static method to update a reactive element
  static updateReactiveElement(
    element: JQuery<Element>,
    url?: string,
    options: { isShared?: boolean } = {}
  ) {
    if (!url) {
      url = element.attr('data-reactive-url');
    }

    if (
      element.data(REACTIVE_UPDATE_REQUEST) &&
      !/modal_background_jobs/.test(url)
    ) {
      debug(`    ${url}: Aborting previous request`);
      element.data(REACTIVE_UPDATE_REQUEST).abort();
      element.data(REACTIVE_UPDATE_REQUEST, null);
    }

    debug(`    Starting reactive request: ${url}`);
    debug(`    ${url}: Starting reactive request`);

    const headers: Record<string, string> = { 'X-Reactive-Update': 'true' };
    if (options.isShared) {
      headers.Authorization = `Bearer ${window.sharingJWT}`;
    }

    return $.ajax({
      url,
      headers,
      beforeSend: () => {
        if (element.attr('data-reactive-show-progress') === 'true') {
          element.prepend(
            `<div class="aha-spinner reactive-in-progress" role="spinner">
              <div class="aha-spinner__body">
                <div class="aha-spinner__icon"></div>
              </div>
            </div>`
          );
        }
      },
      complete: () => {
        element.data(REACTIVE_UPDATE_REQUEST, null);
      },
      error: (xhr, textStatus, errorThrown) => {
        element.trigger('reactive:error', [textStatus, errorThrown]);
      },
      success(data, status, xhr) {
        // sometimes the target isn't in the window document by the time fetch returns and doesn't need to be updated
        if (!element[0]?.isConnected) {
          debug(`    element to update no longer exists`);
          return;
        }
        if (
          xhr.getResponseHeader('Content-Type')?.indexOf('text/javascript') ===
          0 // handle ;charset=utf-8
        ) {
          debug(`    ${url}: Reactive: loaded JS`);
        } else {
          debug(
            `    ${url}: Processing reactive update on ${elementIdentifier(
              element[0]
            )}`
          );

          // Clear out stubbed content
          if (
            element[0] instanceof HTMLElement &&
            (element[0].dataset.reactiveStubbedContent === 'true' ||
              new URL(url, location.href).searchParams.get('initial_load') ===
                'true')
          ) {
            debug(`    ${url}: Inserting stubbed content`);
            $(element[0]).after($.trim(data));
            const newElement = $(element[0]).next();
            $(element[0]).remove();

            // If we have stubbed content with only a limited initial load make another request to get the rest of the content
            if (
              new URL(url, location.href).searchParams.get('initial_load') ===
              'true'
            ) {
              debug(`    ${url}: Fetching full load of stubbed content`);
              updateReactiveParams(newElement, { initial_load: null });
            }

            return;
          }

          // Instantiate the new nodes.
          const newNodes = $($.trim(data));
          const scripts = newNodes
            .find("script:not([type='text/template']):not([type='module'])")
            .addBack("script:not([type='text/template']):not([type='module'])")
            .detach();

          // Patch them into the DOM.
          debug(`    ${url}: Patching content`);
          Sentry.addBreadcrumb({
            message: 'reactive_rails patching content',
            category: 'xhr',
          });
          const patcher = new Patcher(element[0], newNodes[0]);
          patcher.patch();

          // Run the scripts.
          debug(`    ${url}: Executing inline scripts (${scripts.length})`);
          scripts.each(function () {
            // Must call window.eval if we want access to variables that are defined on the `window`.
            // See:
            // https://262.ecma-international.org/5.1/#sec-10.4.2
            // and
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/eval#description:~:text=If%20you%20use%20the%20eval%20function,the%20scope%20where%20it's%20being%20called
            //
            // TLDR; If we use just `eval` rather than `window.eval`, eval is executed with its `this`
            // context being a <script> element rather than `window`, meaning it does not have access
            // to any variables defined outside the `this` of the <script> (namely any global variables).
            try {
              window.eval($(this).html()); // eslint-disable-line no-eval
            } catch (ex) {
              // Catch and log error, log JS
              debug(`    ${url}: Error while executing inline script: ${ex}`);
              console.error(ex);
              Sentry.addBreadcrumb({
                message: 'Error executing reactive script tag',
                category: 'xhr',
                data: {
                  script: $(this).html(),
                },
              });

              Sentry.captureException(ex);
            }
          });

          $('[data-react-class="Selectro"]').trigger('updateProps');

          debug(`    ${url}: Reactive patcher stats: ${patcher.dumpStats()}`);
        }
      },
    });
  }
}

export default Reactor;
